import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import * as Icon from 'react-feather';
import MainStructure from '../components/general/MainStructure';
import { useAuth } from '../AuthContext';
import TimezoneSelector from './components/TimezoneSelector';
import skoolLogo from '../assets/skool.png';
import 'react-datepicker/dist/react-datepicker.css';

// A Skool post goes into one group (community) per Skool account, under one of
// that group's categories, with a required title. Skool has no public API: the
// account is the user's browser session, connected through the SchedPilot
// Chrome extension, and the groups and categories come from Skool through
// api.schedpilot.com (lib/skool.php).

const TITLE_MAX = 150;      // keep in step with SP_SKOOL_TITLE_MAX in lib/skool.php
const BODY_MAX = 10000;     // SP_SKOOL_BODY_MAX
const MAX_IMAGES = 10;      // SP_SKOOL_MAX_IMAGES

const API = 'https://api.schedpilot.com/wp-json/api/v1';
const UPLOAD_URL = 'https://api.schedpilot.com/wp-json/custom/v1/upload-image';

const formatLocalDate = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

async function apiGet(path) {
  try {
    const res = await fetch(API + path, { credentials: 'include' });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok && (data.code ?? 200) < 400, status: res.status, data };
  } catch (e) {
    return { ok: false, status: 0, data: { message: 'Could not reach SchedPilot.' } };
  }
}

const card = { background: '#fff', border: '1px solid #e8edf2', borderRadius: 14, padding: 20, marginBottom: 16 };
const label = { fontWeight: 700, fontSize: '0.95rem', marginBottom: 4, color: '#0f172a' };
const hint = { fontSize: '0.78rem', color: '#64748b', marginBottom: 12 };
const counter = (n, max) => ({ fontSize: '0.72rem', color: n > max ? '#dc2626' : '#94a3b8', textAlign: 'right', marginTop: 4 });

function SkoolPost() {
  const myContext = useAuth();
  const navigate = useNavigate();
  const skoolAccounts = (myContext.accounts || []).filter((a) => a.type === 'skool');

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [images, setImages] = useState([]);             // { key, status, id, url, preview, file }
  const [targets, setTargets] = useState({});           // accountId -> { group_id, group_name, group_slug, label_id, label_name }
  const [groups, setGroups] = useState({});             // accountId -> { status, items, message }
  const [labels, setLabels] = useState({});             // `${accountId}:${groupId}` -> { status, items, message }
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [submitting, setSubmitting] = useState(false);

  const nextKey = useRef(0);
  const imagesRef = useRef(images);
  imagesRef.current = images;
  useEffect(() => () => imagesRef.current.forEach((i) => i.preview && URL.revokeObjectURL(i.preview)), []);

  /* ---------- Groups and categories, loaded from Skool on demand ---------- */

  const loadGroups = useCallback(async (accountId) => {
    setGroups((g) => ({ ...g, [accountId]: { status: 'loading', items: [] } }));
    const r = await apiGet(`/skool/${accountId}/groups`);
    if (r.ok) {
      setGroups((g) => ({ ...g, [accountId]: { status: 'ok', items: r.data.items || [] } }));
    } else {
      if (r.data.error === 'SKOOL_NEEDS_RECONNECT' && myContext.getAccountsData) myContext.getAccountsData();
      setGroups((g) => ({ ...g, [accountId]: { status: 'error', items: [], message: r.data.message || 'Could not load your Skool groups.' } }));
    }
  }, [myContext]);

  const loadLabels = useCallback(async (accountId, group) => {
    const key = `${accountId}:${group.id}`;
    setLabels((l) => ({ ...l, [key]: { status: 'loading', items: [] } }));
    const slug = group.slug ? `?slug=${encodeURIComponent(group.slug)}` : '';
    const r = await apiGet(`/skool/${accountId}/groups/${encodeURIComponent(group.id)}/labels${slug}`);
    setLabels((l) => ({
      ...l,
      [key]: r.ok ? { status: 'ok', items: r.data.items || [] }
                  : { status: 'error', items: [], message: r.data.message || 'Could not load the categories.' },
    }));
  }, []);

  const toggleAccount = (account) => {
    setTargets((t) => {
      const next = { ...t };
      if (next[account.id]) delete next[account.id];
      else next[account.id] = { group_id: '', group_name: '', group_slug: '', label_id: '', label_name: '' };
      return next;
    });
    if (!targets[account.id] && !groups[account.id]) loadGroups(account.id);
  };

  const chooseGroup = (accountId, groupId) => {
    const group = (groups[accountId]?.items || []).find((g) => String(g.id) === groupId);
    setTargets((t) => ({
      ...t,
      [accountId]: { group_id: group ? String(group.id) : '', group_name: group?.name || '', group_slug: group?.slug || '', label_id: '', label_name: '' },
    }));
    if (group && !labels[`${accountId}:${group.id}`]) loadLabels(accountId, group);
  };

  const chooseLabel = (accountId, labelId) => {
    const t = targets[accountId];
    const item = (labels[`${accountId}:${t.group_id}`]?.items || []).find((l) => String(l.id) === labelId);
    setTargets((all) => ({ ...all, [accountId]: { ...all[accountId], label_id: item ? String(item.id) : '', label_name: item?.name || '' } }));
  };

  /* ---------- Images ---------- */

  const patchImage = (key, patch) => setImages((prev) => prev.map((i) => (i.key === key ? { ...i, ...patch } : i)));

  const uploadInto = useCallback(async (key, file) => {
    const form = new FormData();
    form.append('image', file);
    try {
      const res = await fetch(UPLOAD_URL, { method: 'POST', credentials: 'include', body: form });
      const data = await res.json();
      if (data.code === 200 && data.image_id) patchImage(key, { status: 'done', id: data.image_id, url: data.file_url });
      else patchImage(key, { status: 'error' });
    } catch (e) {
      patchImage(key, { status: 'error' });
    }
  }, []);

  const onDrop = useCallback((accepted, rejected) => {
    if (rejected.length) toast.error('Only JPG, PNG and WebP images can be attached.');
    const room = MAX_IMAGES - imagesRef.current.length;
    if (room <= 0) return toast.error(`A Skool post can carry at most ${MAX_IMAGES} images.`);
    const fresh = accepted.slice(0, room).map((file) => ({
      key: ++nextKey.current, status: 'uploading', id: null, url: null, preview: URL.createObjectURL(file), file,
    }));
    setImages((prev) => [...prev, ...fresh]);
    fresh.forEach((i) => uploadInto(i.key, i.file));
  }, [uploadInto]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, multiple: true, accept: { 'image/jpeg': [], 'image/png': [], 'image/webp': [] }, disabled: images.length >= MAX_IMAGES,
  });

  const removeImage = (key) => setImages((prev) => {
    const gone = prev.find((i) => i.key === key);
    if (gone?.preview) URL.revokeObjectURL(gone.preview);
    return prev.filter((i) => i.key !== key);
  });

  /* ---------- What still stands between the user and scheduling ---------- */

  const chosen = skoolAccounts.filter((a) => targets[a.id]);
  const problems = [];
  if (!title.trim()) problems.push('Add a title');
  if (title.length > TITLE_MAX) problems.push(`Shorten the title to ${TITLE_MAX} characters`);
  if (body.length > BODY_MAX) problems.push(`Shorten the post to ${BODY_MAX} characters`);
  if (images.some((i) => i.status === 'uploading')) problems.push('Wait for uploads to finish');
  if (images.some((i) => i.status === 'error')) problems.push('Remove the images that failed to upload');
  if (!chosen.length) problems.push('Choose a Skool account');
  chosen.forEach((a) => {
    const t = targets[a.id];
    if (a.needs_reconnect) problems.push(`Reconnect ${a.name} from the SchedPilot extension`);
    else if (!t.group_id) problems.push(`Choose a group for ${a.name}`);
    else {
      const l = labels[`${a.id}:${t.group_id}`];
      if (l?.status === 'ok' && l.items.length && !t.label_id) problems.push(`Choose a category for ${a.name}`);
    }
  });

  const submit = async (postStatus) => {
    if (problems.length) return toast.error(problems[0]);
    const m = Array.isArray(myContext.membership) ? myContext.membership[0] : myContext.membership;
    const isLifetime = m?.plan?.type === 'lifetime' || (
      m?.end_date_time
        ? new Date(m.end_date_time.replace(' ', 'T') + 'Z').getTime() > Date.now() + 10 * 365 * 24 * 3600 * 1000
        : false
    );
    if (!myContext.isValidMembership() && !isLifetime) {
      return toast.error('Your membership is expired. It cannot post.');
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/create-skool-post`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: title.trim(),
          text: body,
          images: images.map((i) => i.id),
          targets: chosen.map((a) => ({ account_id: a.id, ...targets[a.id] })),
          scheduledDate: formatLocalDate(selectedDate),
          timezone,
          postStatus,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.code !== 200) return toast.error(data.message || 'Could not schedule the post.');

      if (postStatus === 'draft') {
        toast.success('Skool post saved as a draft.');
        if (myContext.addDraftItem) myContext.addDraftItem({ pid: data.data?.id, content: body, scheduled_date: selectedDate });
        navigate('/drafts');
      } else {
        toast.success('Skool post scheduled.');
        if (myContext.addScheduledItem) myContext.addScheduledItem(data.data);
        navigate('/schedule-posts');
      }
    } catch (e) {
      toast.error('Error connecting to the server. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MainStructure activeItem={'write-post'}>
      <div className="container p-0">
        {myContext.returnMembershipNotification && myContext.returnMembershipNotification()}

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <button type="button" onClick={() => navigate('/write-post')} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', fontWeight: 500 }}>
            <Icon.ArrowLeft size={14} /> Back
          </button>
          <div>
            <h1 style={{ fontWeight: 800, fontSize: '1.4rem', margin: 0, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
              <img src={skoolLogo} alt="" style={{ width: 22, height: 22, borderRadius: 5 }} /> Skool Post
            </h1>
            <div style={{ fontSize: '0.8rem', color: '#64748b' }}>A post in your Skool community, published at the time you choose.</div>
          </div>
        </div>

        <div className="row g-4">
          <div className="col-12 col-lg-8">

            {/* Where */}
            <div style={card}>
              <div style={label}>Post to</div>
              {skoolAccounts.length === 0 ? (
                <div style={{ ...hint, marginBottom: 0 }}>
                  No Skool account connected yet.{' '}
                  <button type="button" onClick={() => navigate('/social-accounts')} style={{ border: 'none', background: 'none', padding: 0, color: '#0f172a', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}>Connect Skool</button>
                </div>
              ) : skoolAccounts.map((a) => {
                const on = !!targets[a.id];
                const g = groups[a.id];
                const t = targets[a.id];
                const l = t?.group_id ? labels[`${a.id}:${t.group_id}`] : null;
                return (
                  <div key={a.id} style={{ border: '1px solid ' + (on ? '#cbd5e1' : '#f1f5f9'), borderRadius: 10, padding: '10px 12px', marginBottom: 8, background: on ? '#f8fafc' : '#fff' }}>
                    <label htmlFor={`skool-acc-${a.id}`} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', margin: 0 }}>
                      <input id={`skool-acc-${a.id}`} type="checkbox" checked={on} onChange={() => toggleAccount(a)} style={{ width: 16, height: 16 }} />
                      <span style={{ fontWeight: 600, fontSize: '0.88rem', flex: 1 }}>{a.name}</span>
                      {a.needs_reconnect && <span style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 6, padding: '1px 8px', fontSize: '0.72rem', fontWeight: 600 }}>Needs reconnect</span>}
                    </label>

                    {on && !a.needs_reconnect && (
                      <div className="row g-2" style={{ marginTop: 6 }}>
                        <div className="col-12 col-md-6">
                          <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 2 }}>Group</div>
                          {g?.status === 'error' ? (
                            <div style={{ fontSize: '0.78rem', color: '#b91c1c' }}>
                              {g.message} <button type="button" onClick={() => loadGroups(a.id)} style={{ border: 'none', background: 'none', color: '#0f172a', textDecoration: 'underline', padding: 0, fontSize: '0.78rem' }}>Retry</button>
                            </div>
                          ) : (
                            <select className="form-select form-select-sm" value={t.group_id} disabled={!g || g.status === 'loading'} onChange={(e) => chooseGroup(a.id, e.target.value)}>
                              <option value="">{!g || g.status === 'loading' ? 'Loading groups…' : 'Choose a group'}</option>
                              {(g?.items || []).map((x) => <option key={x.id} value={String(x.id)}>{x.name}</option>)}
                            </select>
                          )}
                        </div>
                        <div className="col-12 col-md-6">
                          <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 2 }}>Category</div>
                          {l?.status === 'error' ? (
                            <div style={{ fontSize: '0.78rem', color: '#b91c1c' }}>{l.message}</div>
                          ) : l?.status === 'ok' && !l.items.length ? (
                            <div style={{ fontSize: '0.78rem', color: '#94a3b8', paddingTop: 4 }}>This group has no categories.</div>
                          ) : (
                            <select className="form-select form-select-sm" value={t.label_id} disabled={!t.group_id || !l || l.status === 'loading'} onChange={(e) => chooseLabel(a.id, e.target.value)}>
                              <option value="">{!t.group_id ? 'Choose a group first' : !l || l.status === 'loading' ? 'Loading categories…' : 'Choose a category'}</option>
                              {(l?.items || []).map((x) => <option key={x.id} value={String(x.id)}>{x.name}</option>)}
                            </select>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* What */}
            <div style={card}>
              <div style={label}>Title</div>
              <input type="text" className="form-control" value={title} maxLength={TITLE_MAX + 20} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Week 3: your first 100 members" />
              <div style={counter(title.length, TITLE_MAX)}>{title.length}/{TITLE_MAX}</div>

              <div style={{ ...label, marginTop: 14 }}>Post</div>
              <textarea className="form-control" rows={8} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write your post…" />
              <div style={counter(body.length, BODY_MAX)}>{body.length}/{BODY_MAX}</div>
            </div>

            {/* Images */}
            <div style={card}>
              <div style={label}>Images <span style={{ fontWeight: 500, color: '#94a3b8' }}>({images.length}/{MAX_IMAGES}, optional)</span></div>
              {images.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 10, marginBottom: 12 }}>
                  {images.map((i, idx) => (
                    <div key={i.key} style={{ position: 'relative', aspectRatio: '1 / 1', borderRadius: 10, overflow: 'hidden', border: '1px solid ' + (i.status === 'error' ? '#fecaca' : '#e2e8f0') }}>
                      <img src={i.preview || i.url} alt={`Attachment ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: i.status === 'done' ? 1 : 0.45 }} />
                      <button type="button" aria-label={`Remove image ${idx + 1}`} onClick={() => removeImage(i.key)} style={{ position: 'absolute', top: 6, right: 6, width: 24, height: 24, borderRadius: '50%', border: 'none', background: 'rgba(15,23,42,0.75)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon.X size={13} /></button>
                      {i.status === 'uploading' && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 600 }}>Uploading…</div>}
                      {i.status === 'error' && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700, color: '#b91c1c', background: 'rgba(254,242,242,0.85)' }}>Upload failed</div>}
                    </div>
                  ))}
                </div>
              )}
              {images.length < MAX_IMAGES && (
                <div {...getRootProps()} style={{ border: `2px dashed ${isDragActive ? '#0f172a' : '#cbd5e1'}`, background: '#f8fafc', borderRadius: 12, padding: 18, textAlign: 'center', cursor: 'pointer' }}>
                  <input {...getInputProps()} />
                  <Icon.UploadCloud size={20} color="#0f172a" />
                  <div style={{ fontWeight: 600, fontSize: '0.85rem', marginTop: 4 }}>{isDragActive ? 'Drop the images here' : 'Drag images here, or click to choose'}</div>
                  <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>JPG, PNG or WebP</div>
                </div>
              )}
            </div>

            {/* When */}
            <div style={card}>
              <div style={label}>When</div>
              <DatePicker selected={selectedDate} onChange={(d) => setSelectedDate(d)} showTimeSelect dateFormat="MMMM d, yyyy h:mm aa" className="form-control mb-3" />
              <TimezoneSelector value={timezone} onChange={setTimezone} label="Scheduling timezone" />

              {problems.length > 0 && (
                <div style={{ marginTop: 14, padding: '10px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: '0.8rem', color: '#475569' }}>
                  {problems.map((p) => <div key={p} style={{ display: 'flex', gap: 6, alignItems: 'center' }}><Icon.Circle size={8} /> {p}</div>)}
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                <button type="button" disabled={submitting || problems.length > 0} onClick={() => submit('publish')}
                  style={{ flex: 1, background: '#0f172a', border: 'none', borderRadius: 9, padding: '11px 0', fontWeight: 600, fontSize: '0.9rem', color: '#fff', cursor: submitting || problems.length ? 'not-allowed' : 'pointer', opacity: submitting || problems.length ? 0.55 : 1 }}>
                  {submitting ? 'Scheduling…' : 'Schedule post'}
                </button>
                <button type="button" disabled={submitting || problems.length > 0} onClick={() => submit('draft')}
                  style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 9, padding: '11px 16px', fontWeight: 600, fontSize: '0.9rem', color: '#334155', cursor: submitting || problems.length ? 'not-allowed' : 'pointer', opacity: submitting || problems.length ? 0.55 : 1 }}>
                  Save draft
                </button>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-4">
            <div style={{ ...card, position: 'sticky', top: 16, fontSize: '0.8rem', color: '#64748b' }}>
              <div style={{ ...label, fontSize: '0.88rem' }}>How Skool posting works</div>
              <p style={{ marginBottom: 8 }}>Skool has no official API. SchedPilot posts with the Skool login of the browser where you connected the SchedPilot extension, at the time you choose.</p>
              <p style={{ marginBottom: 8 }}>Keep that browser logged in to skool.com. If Skool logs it out, the account shows <strong>Needs reconnect</strong> and you reconnect from the extension.</p>
              <p style={{ margin: 0 }}>Each Skool account posts into one group per post. To post into another group, schedule a second post.</p>
            </div>
          </div>
        </div>
      </div>
    </MainStructure>
  );
}

export default SkoolPost;
